import { Dispatch, FC, SetStateAction, useState } from 'react'
import { Box, IconButton, Typography } from '@mui/material'

import {
  DEVICES_COLUMN_NAME,
  DevicesDataItem,
  IDENTITY_COLUMN_NAME,
  IntegrationInstance,
  MetricManifest,
  ModelManifest
} from 'models'

import { Drawer } from './dataDrawer.styles'
import RowItem from './RowItem'
import { CloseDrawerIcon } from 'ui/components/svg'

type DataItem = DevicesDataItem & { [key in IDENTITY_COLUMN_NAME]?: any }

const INTERNAL_FIELDS = new Set(['SK_ID', 'IS_EXCLUDED', 'MATCHED_RULES'])

interface Props {
  drawerOpen: boolean
  activeRowItem: DevicesDataItem | null
  setDrawerOpen: Dispatch<SetStateAction<boolean>>
  manifest?: MetricManifest
  snowflakeManifest: ModelManifest | null
  dataSourceOptions: IntegrationInstance[]
}

const DataDrawer: FC<Props> = ({
  drawerOpen,
  activeRowItem,
  setDrawerOpen,
  manifest,
  snowflakeManifest,
  dataSourceOptions
}) => {
  const [showMoreOpen, setShowMoreOpen] = useState(false)

  if (!activeRowItem || !manifest?.druid_spec?.record_batch_spec?.input_schema) return <></>

  const availableColumns = manifest.druid_spec.record_batch_spec.input_schema
    .map((schemaItem) => ({
      name: schemaItem.name,
      displayName: schemaItem.displayName || schemaItem.name
    }))
    .filter((column) => {
      if (INTERNAL_FIELDS.has(column.name)) return false
      const value = (activeRowItem as any)[column.name]
      return value !== null && value !== undefined && value !== ''
    })

  const getDisplayValue = (columnName: string, value: any) => {
    if (
      columnName === DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE ||
      columnName === IDENTITY_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE
    ) {
      const dataSource = dataSourceOptions.find((opt) => opt.instance_name === value)
      return dataSource?.name || value
    }

    return value
  }

  return (
    <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
      <Box role="presentation">
        <Box className="wrapper-inner">
          <Box className="header">
            <Typography className="title">{snowflakeManifest?.title} Information</Typography>
            <IconButton className="close-icon" onClick={() => setDrawerOpen(false)}>
              <CloseDrawerIcon />
            </IconButton>
          </Box>

          <Box className="content">
            {availableColumns.map((column) => {
              const item = (activeRowItem as DataItem)[column.name as keyof DataItem]

              const emailItem =
                column.name === 'EMAIL_ADDRESSES' ? (activeRowItem as any).EMAILS_ADDRESSES || item : item

              let displayItem = column.name === 'EMAIL_ADDRESSES' ? emailItem : item

              if (
                column.name === 'IP_ADDRESSES' &&
                typeof displayItem === 'string' &&
                displayItem.includes(']') &&
                displayItem.includes(',')
              ) {
                const parsedArr: string[] = JSON.parse(displayItem)
                const collapsedArr: string[] = parsedArr.slice(0, 3)

                return (
                  <Box className="row-item" key={column.name}>
                    <Typography className="row-name">{column.displayName}</Typography>
                    <Box className="row-data">
                      {parsedArr.length > 3 ? (
                        <>
                          {!showMoreOpen
                            ? collapsedArr.map((item) => <RowItem key={item} text={item} />)
                            : parsedArr.map((item) => <RowItem key={item} text={item} />)}
                        </>
                      ) : (
                        parsedArr.length <= 3 && parsedArr.map((item) => <RowItem key={item} text={item} />)
                      )}

                      {parsedArr.length > 3 && (
                        <Typography className="show-more-button" onClick={() => setShowMoreOpen(!showMoreOpen)}>
                          {showMoreOpen ? 'show less' : 'show more'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )
              }

              if (displayItem) {
                let parsedArray

                if (Array.isArray(displayItem)) {
                  parsedArray = displayItem
                } else if (typeof displayItem === 'string' && displayItem.includes(',')) {
                  try {
                    parsedArray = JSON.parse(displayItem)
                    if (!Array.isArray(parsedArray)) {
                      parsedArray = [parsedArray]
                    }
                  } catch (error) {
                    parsedArray = [displayItem]
                  }
                } else {
                  parsedArray = [String(displayItem)]
                }

                if (parsedArray.length > 1) {
                  return (
                    <Box className="row-item" key={column.name}>
                      <Typography className="row-name">{column.displayName}</Typography>
                      <Box className="row-data" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {parsedArray.map((item, index) => (
                          <RowItem
                            key={`${item}-${index}`}
                            integrationName={column.name === 'ONYXIA_PRODUCT_NAME' ? (item as string) : undefined}
                            text={
                              column.name === 'ONYXIA_PRODUCT_NAME' && manifest.slug === 'user'
                                ? getDisplayValue(
                                    DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE,
                                    (activeRowItem as DataItem).ONYXIA_PRODUCT_INSTANCE
                                  ) || item
                                : typeof item === 'boolean'
                                ? item
                                  ? 'Managed'
                                  : 'Non-Managed'
                                : item
                            }
                          />
                        ))}
                      </Box>
                    </Box>
                  )
                }

                displayItem = parsedArray[0]
              }

              if (displayItem) {
                const finalDisplayValue = getDisplayValue(column.name, displayItem)

                return (
                  <Box className="row-item" key={column.name}>
                    <Typography className="row-name">{column.displayName}</Typography>
                    <Box className="row-data">
                      <RowItem
                        integrationName={
                          column.name === 'ONYXIA_PRODUCT_NAME' ? (finalDisplayValue as string) : undefined
                        }
                        text={
                          column.name === 'ONYXIA_PRODUCT_NAME' && manifest.slug === 'user'
                            ? getDisplayValue(
                                DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE,
                                (activeRowItem as DataItem).ONYXIA_PRODUCT_INSTANCE
                              ) || finalDisplayValue
                            : typeof finalDisplayValue === 'boolean'
                            ? finalDisplayValue
                              ? 'Managed'
                              : 'Non-Managed'
                            : finalDisplayValue
                        }
                      />
                    </Box>
                  </Box>
                )
              }

              return null
            })}
          </Box>
        </Box>
      </Box>
    </Drawer>
  )
}

export default DataDrawer
